import { t } from "@lingui/macro";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useMemo } from "react";

import {
  DropdownSelect,
  DropdownSelectOption,
} from "@whoosh/design-system/src/components/controls";
import { ClockIcon } from "@whoosh/design-system/src/components/icons_v2/general";
import {
  VStack,
  HStack,
  Box,
  Text,
} from "@whoosh/design-system/src/components/primitive";

import { setBookingDurationAtom } from "../../../../../atoms/actions/setBookingDurationAtom/setBookingDurationAtom";
import { currentUserIsBookingOwnerAtom } from "../../../../../atoms/selectors/currentUserIsBookingOwnerAtom/currentUserIsBookingOwnerAtom";
import { getBookingDurationAtom } from "../../../../../atoms/selectors/getBookingDurationAtom/getBookingDurationAtom";
import { isAlreadyPaidAtom } from "../../../../../atoms/selectors/isAlreadyPaidAtom/isAlreadyPaidAtom";
import { bookingCourtAtom } from "../atoms/stores";
import { useGetCourseOptions_CourseBucket$key } from "../hooks/__generated__/useGetCourseOptions_CourseBucket.graphql";
import { useGetCourseOptions } from "../hooks/useGetCourseOptions";

type Props = {
  courseBucketRef: useGetCourseOptions_CourseBucket$key | null;
  durationMins: number;
  durationOptions: DropdownSelectOption<number>[];
};

export const BookingDurationControl = ({
  courseBucketRef,
  durationMins,
  durationOptions,
}: Props) => {
  const currentUserIsBookingOwner = useAtomValue(currentUserIsBookingOwnerAtom);

  const duration = useAtomValue(getBookingDurationAtom);
  const setDuration = useSetAtom(setBookingDurationAtom);
  // Set the initial duration to the original duration if it's null
  useMemo(() => {
    if (duration === null) {
      setDuration({ durationMins: durationMins });
    }
  }, [duration, setDuration, durationMins]);

  const [court, setCourt] = useAtom(bookingCourtAtom);

  const availableCourts = useGetCourseOptions({
    courseBucketRef: courseBucketRef,
  });

  const isAlreadyPaid = useAtomValue(isAlreadyPaidAtom);

  return durationOptions.length > 0 ? (
    <VStack padding={5} gap={5} backgroundColor="surface-primary" marginTop={2}>
      <HStack alignItems="center" gap={3}>
        <Box
          backgroundColor="surface-main-primary-subtlest"
          width={24}
          height={24}
          justifyContent="center"
          alignItems="center"
          style={{ borderRadius: 4 }}
        >
          <ClockIcon />
        </Box>
        <Text>{t`Booking Duration`}</Text>
      </HStack>
      <VStack flex={1}>
        <DropdownSelect<number>
          isViewOnly={!currentUserIsBookingOwner || isAlreadyPaid}
          hideOuterMargin
          testID="booking-duration-dropdown"
          hideChevron
          includeLeftElementInSelectedValue
          contentSizeOverride="matchTrigger"
          options={durationOptions}
          value={duration ?? undefined}
          onChange={(duration) => {
            // If we have a selected court, check if the new duration is valid for it
            if (court) {
              // Get the max duration for the current court
              const courtMaxDuration = court.maxDurationMins;

              // If the selected duration exceeds the court's max duration,
              // we need to find a court that supports this duration
              if (Number(duration) > courtMaxDuration) {
                // Find a court that supports the selected duration
                const validCourts = availableCourts?.filter(
                  (availableCourt) =>
                    availableCourt.maxDurationMins >= Number(duration)
                );

                // If we found valid courts, select the first one
                if (validCourts && validCourts.length > 0) {
                  setCourt(validCourts[0]);
                }
              }
            }
            setDuration({ durationMins: Number(duration) });
          }}
        />
      </VStack>
    </VStack>
  ) : null;
};
